import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { fetchMyPermissions } from '../lib/queries/rbac';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

// ── Types ────────────────────────────────────────────────────

type EnforcementMode = 'permissive' | 'strict';

interface PermissionsContextType {
  permissions: Set<string>;
  loadingPermissions: boolean;
  hasPermission: (action: string) => boolean;
  hasAnyPermission: (actions: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
  enforcementMode: EnforcementMode;
}

// ── Legacy role-based fallback (permissive mode only) ────────
// @deprecated LEGACY_ROLE_PERMISSION_FALLBACK will be removed once all guards
// are migrated to DB-driven permission checks. In strict mode it is never used.
// New code: use usePermissions() from src/hooks/usePermissions.ts + PERMISSIONS registry.
//
// HIGH_RISK_FALLBACK_EXCLUSIONS: permission prefixes that must NEVER be granted
// via the role fallback — only real DB assignments count for these.
const HIGH_RISK_FALLBACK_EXCLUSIONS = [
  'system.rbac.',
  'manage_rbac',
  'finance.journal.post',
  'finance.payments.approve',
  'finance.invoices.approve',
  'finance.vat.manage',
  'finance.intercompany.manage',
  // 'system.users.' — planned for next sprint after DB permission validation
  // 'system.config.' — planned for next sprint after DB permission validation
];

const LEGACY_ROLE_PERMISSION_FALLBACK: Record<string, string[]> = {
  super_admin: ['*'],
  it_admin: ['system.', 'admin.'],
  hr_manager: ['hr.', 'self.'],
  hr_staff: ['hr.', 'self.'],
  chro: ['hr.', 'self.', 'executive.'],
  finance_manager: ['finance.', 'hr.payroll.', 'self.'],
  finance_ap: ['finance.ap.', 'finance.gl.', 'finance.statements.', 'self.'],
  finance_ar: ['finance.ar.', 'finance.gl.', 'finance.statements.', 'self.'],
  cfo: ['finance.', 'executive.', 'self.'],
  sales_rep: ['sales.', 'self.'],
  sales_manager: ['sales.', 'self.'],
  // Sales Pilot roles: deliberately narrowed fallback — never bare 'sales.' (that
  // would imply sales.orders./sales.customers.manage in a degraded-RBAC window).
  sales_rep_pilot: ['sales.crm.', 'sales.quotations.', 'sales.dashboard.', 'sales.analytics.', 'sales.customers.view', 'sales.products.view', 'sales.targets.view', 'self.'],
  sales_manager_pilot: ['sales.crm.', 'sales.quotations.', 'sales.dashboard.', 'sales.analytics.', 'sales.customers.view', 'sales.products.view', 'sales.targets.view', 'self.'],
  solution_consultant: ['sales.', 'self.'],
  cbo: ['sales.', 'executive.', 'self.'],
  ceo_commercial: ['sales.', 'finance.', 'executive.', 'self.'],
  ceo_manufacturing: ['manufacturing.', 'finance.', 'executive.', 'self.'],
  group_ceo: ['executive.', 'self.'],
  coo: ['executive.', 'self.'],
  procurement_manager: ['procurement.', 'self.'],
  purchasing_officer: ['procurement.', 'self.'],
  warehouse_manager: ['warehouse.', 'self.'],
  warehouse_staff: ['warehouse.', 'self.'],
  production_manager: ['manufacturing.', 'self.'],
  production_staff: ['manufacturing.', 'self.'],
  logistics_manager: ['logistics.', 'warehouse.', 'self.'],
  logistics_staff: ['logistics.', 'warehouse.', 'self.'],
  quality_manager: ['quality.', 'manufacturing.', 'self.'],
  qa_inspector: ['quality.', 'self.'],
  engineering: ['manufacturing.', 'sales.products.', 'self.'],
  engineering_manager: ['manufacturing.', 'sales.products.', 'self.'],
  project_manager: ['project.', 'self.'],
  pmo_head: ['project.', 'self.'],
  maintenance_tech: ['manufacturing.', 'hse.', 'self.'],
  compliance_officer: ['compliance.', 'hr.compliance.', 'self.'],
  internal_auditor: ['finance.', 'procurement.', 'project.', 'self.'],
  pro: ['compliance.', 'hr.documents.', 'self.'],
  marketing: ['sales.crm.', 'sales.targets.', 'self.'],
  team_lead: ['hr.employees.view', 'hr.performance.', 'self.'],
  employee: ['self.'],
};

function roleFallbackHasPermission(role: string, action: string): boolean {
  // Never allow high-risk permissions via fallback — only real DB assignments count
  if (HIGH_RISK_FALLBACK_EXCLUSIONS.some(excl => action.startsWith(excl) || action === excl)) {
    return false;
  }
  const prefixes = LEGACY_ROLE_PERMISSION_FALLBACK[role];
  if (!prefixes) return false;
  if (prefixes.includes('*')) return true;
  const matched = prefixes.some(prefix => action.startsWith(prefix));
  if (matched && import.meta.env.DEV) {
    console.warn(
      `[RBAC] Legacy role fallback used for "${action}" (role: ${role}). ` +
      'Ensure this role has the DB permission assigned. ' +
      'Remove before external-client readiness.',
    );
  }
  return matched;
}

// ── Context ──────────────────────────────────────────────────

const PermissionsContext = createContext<PermissionsContextType | null>(null);

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider');
  return ctx;
}

// ── Migration guide ───────────────────────────────────────────
// CURRENT:  import { usePermissions } from '@/contexts/PermissionsContext'
//           const { hasPermission } = usePermissions();
//           hasPermission('some.action')
//
// PREFERRED (new code): Use the dedicated hook and permission service instead:
//           import { usePermissions } from '@/hooks/usePermissions';
//           import { PERMISSIONS } from '@/lib/permissions/permissionKeys';
//           const { hasPermission } = usePermissions();
//           hasPermission(PERMISSIONS.FINANCE.POST_JOURNAL)
//
// The hook-based approach:
//   1. Is typed against the real permission key registry
//   2. Uses get_my_permissions_scoped() (company-aware)
//   3. Warns in dev mode about unknown keys
//   4. Does NOT depend on ROLE_PERMISSION_FALLBACK
// ─────────────────────────────────────────────────────────────

/**
 * Returns true when RBAC_ENFORCEMENT_MODE is 'strict'.
 * Components can use this to conditionally adjust UI behavior.
 */
export function useIsStrictMode(): boolean {
  const ctx = useContext(PermissionsContext);
  return ctx?.enforcementMode === 'strict';
}

// ── Provider ─────────────────────────────────────────────────

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [enforcementMode, setEnforcementMode] = useState<EnforcementMode>('permissive');

  // Load enforcement mode from system_feature_flags on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('system_feature_flags')
          .select('value')
          .eq('key', 'RBAC_ENFORCEMENT_MODE')
          .maybeSingle();
        if (!cancelled && data?.value) {
          setEnforcementMode(data.value === 'strict' ? 'strict' : 'permissive');
        }
      } catch {
        // Default to permissive on error — safe fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load user permissions
  const loadPermissions = useCallback(async () => {
    if (!user || !profile) {
      setPermissions(new Set());
      setLoadingPermissions(false);
      return;
    }
    setLoadingPermissions(true);
    try {
      const perms = await fetchMyPermissions();
      setPermissions(new Set(perms));
    } catch (err) {
      console.error('[RBAC] Failed to load permissions:', err);
      setPermissions(new Set());
    } finally {
      setLoadingPermissions(false);
    }
  }, [user?.id, profile?.id]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Core permission check
  const hasPermission = useCallback((action: string): boolean => {
    // Guard: detect missing permission keys
    if (action === undefined || action === null || action === '') {
      if (import.meta.env.DEV) {
        console.warn(
          '[RBAC] hasPermission() called with undefined/null/empty key.',
          'This indicates a navigation item or component missing its permissionKey.',
          new Error().stack,
        );
      } else {
        // Report to Sentry in production (no-op when VITE_SENTRY_DSN is unset).
        // Replaces the prior dead `window.Sentry?.captureMessage` reference
        // which only existed in case Sentry happened to be injected via a CDN.
        import('../lib/monitoring/sentry')
          .then(({ captureMessage }) =>
            captureMessage('[RBAC] hasPermission called with undefined/null key', 'warning'))
          .catch(() => { /* never let monitoring break the guard */ });
      }
      // Permissive: allow (don't break UI). Strict: deny.
      return enforcementMode === 'permissive';
    }

    // DB-driven permission check
    const hasDbPermission = permissions.has(action);

    if (enforcementMode === 'strict') {
      // Strict mode: ONLY the DB permission set matters
      return hasDbPermission;
    }

    // Permissive mode: DB permission OR role-based fallback
    if (hasDbPermission) return true;
    const role = profile?.role;
    if (!role) return false;
    return roleFallbackHasPermission(role, action);
  }, [permissions, enforcementMode, profile?.role]);

  const hasAnyPermission = useCallback((actions: string[]): boolean => {
    return actions.some(a => hasPermission(a));
  }, [hasPermission]);

  const refreshPermissions = loadPermissions;

  return (
    <PermissionsContext.Provider value={{
      permissions,
      loadingPermissions,
      hasPermission,
      hasAnyPermission,
      refreshPermissions,
      enforcementMode,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}
